import spanishMessages from 'ra-language-spanish';
import common from './common';
import auth from './auth';
import dashboard from './dashboard';
import qrphoneResources from './qrphone';
import onboarding from './onboarding';

/**
 * `ra-language-spanish` is stuck at v1.0.0 (last updated 2017) and
 * was never refreshed for react-admin v4 / v5. Many keys the modern
 * react-admin chrome needs (`ra.navigation.page_rows_per_page`,
 * `ra.sort.*`, `ra.saved_queries.*`, `ra.configurable.*`, etc.) are
 * absent and surface as raw key strings in the UI.
 *
 * Fill those gaps inline so the package keeps providing the keys it
 * does have (the bulk) while we override the v5 additions in formal
 * Spain Spanish ("usted"). Mirrored against `ra-language-english@5.0.4`.
 */
const v5RaSpanishOverrides = {
  navigation: {
    no_results: 'No se encontraron resultados',
    no_more_results:
      'La página número %{page} está fuera de los límites. Pruebe con la página anterior.',
    page_out_of_boundaries: 'La página número %{page} está fuera de los límites',
    page_out_from_end: 'No se puede navegar después de la última página',
    page_out_from_begin: 'No se puede navegar antes de la página 1',
    page_range_info: '%{offsetBegin}–%{offsetEnd} de %{total}',
    partial_page_range_info: '%{offsetBegin}–%{offsetEnd} de más de %{offsetEnd}',
    current_page: 'Página %{page}',
    page: 'Ir a la página %{page}',
    first: 'Ir a la primera página',
    last: 'Ir a la última página',
    next: 'Ir a la página siguiente',
    previous: 'Ir a la página anterior',
    page_rows_per_page: 'Filas por página:',
    skip_nav: 'Saltar al contenido',
  },
  sort: {
    sort_by: 'Ordenar por %{field} %{order}',
    ASC: 'ascendente',
    DESC: 'descendente',
  },
  saved_queries: {
    label: 'Consultas guardadas',
    query_name: 'Nombre de la consulta',
    new_label: 'Guardar consulta actual…',
    new_dialog_title: 'Guardar la consulta actual como',
    remove_label: 'Eliminar consulta guardada',
    remove_label_with_name: 'Eliminar la consulta «%{name}»',
    remove_dialog_title: '¿Eliminar la consulta guardada?',
    remove_message: '¿Seguro que desea eliminar este filtro de su lista de consultas guardadas?',
    help: 'Filtre la lista y guarde esta consulta para más tarde',
  },
  configurable: {
    customize: 'Personalizar',
    configureMode: 'Configurar esta página',
    inspector: {
      title: 'Inspector',
      content: 'Pase el ratón sobre los elementos de la aplicación para configurarlos',
      reset: 'Restablecer ajustes',
      hideAll: 'Ocultar todo',
      showAll: 'Mostrar todo',
    },
    Datagrid: {
      title: 'Cuadrícula de datos',
      unlabeled: 'Columna sin etiqueta #%{column}',
    },
    SimpleForm: {
      title: 'Formulario',
      unlabeled: 'Campo sin etiqueta #%{input}',
    },
    SimpleList: {
      title: 'Lista simple',
      primaryText: 'Texto principal',
      secondaryText: 'Texto secundario',
      tertiaryText: 'Texto terciario',
    },
  },
  action: {
    cancel: 'Cancelar',
    clear_array_input: 'Vaciar lista',
    clear_input_value: 'Limpiar campo',
    clone: 'Clonar',
    confirm: 'Confirmar',
    create: 'Crear',
    create_item: 'Crear %{item}',
    delete: 'Eliminar',
    edit: 'Editar',
    export: 'Exportar',
    list: 'Listar',
    refresh: 'Actualizar',
    remove_filter: 'Eliminar este filtro',
    remove_all_filters: 'Eliminar todos los filtros',
    remove: 'Eliminar',
    save: 'Guardar',
    search: 'Buscar',
    select_all: 'Seleccionar todo',
    select_all_button: 'Seleccionar todo',
    select_row: 'Seleccionar esta fila',
    show: 'Mostrar',
    sort: 'Ordenar',
    undo: 'Deshacer',
    unselect: 'Deseleccionar',
    expand: 'Expandir',
    close: 'Cerrar',
    close_menu: 'Cerrar menú',
    open_menu: 'Abrir menú',
    update: 'Actualizar',
    move_up: 'Mover arriba',
    move_down: 'Mover abajo',
    open: 'Abrir',
    toggle_theme: 'Cambiar tema claro/oscuro',
    select_columns: 'Columnas',
    update_application: 'Recargar la aplicación',
    add_filter: 'Añadir filtro',
    add: 'Añadir',
    back: 'Volver',
  },
  message: {
    about: 'Acerca de',
    are_you_sure: '¿Está seguro?',
    auth_error: 'Se ha producido un error al validar el token de autenticación.',
    bulk_delete_content:
      '¿Seguro que desea eliminar este %{name}? |||| ¿Seguro que desea eliminar estos %{smart_count} elementos?',
    bulk_delete_title:
      'Eliminar %{name} |||| Eliminar %{smart_count} %{name}',
    bulk_update_content:
      '¿Seguro que desea actualizar este %{name}? |||| ¿Seguro que desea actualizar estos %{smart_count} elementos?',
    bulk_update_title:
      'Actualizar %{name} |||| Actualizar %{smart_count} %{name}',
    clear_array_input: '¿Seguro que desea vaciar toda la lista?',
    delete_content: '¿Seguro que desea eliminar este elemento?',
    delete_title: 'Eliminar %{name} #%{id}',
    details: 'Detalles',
    error: 'Se ha producido un error en el cliente y su solicitud no se ha podido completar.',
    invalid_form: 'El formulario no es válido. Compruebe si hay errores.',
    loading: 'Cargando, espere por favor.',
    no: 'No',
    not_found: 'O bien ha introducido una URL incorrecta o ha seguido un enlace defectuoso.',
    yes: 'Sí',
    unsaved_changes: 'Algunos de sus cambios no se han guardado. ¿Seguro que desea ignorarlos?',
  },
  input: {
    file: {
      upload_several: 'Arrastre algunos archivos hasta aquí o haga clic para seleccionar uno.',
      upload_single: 'Arrastre un archivo hasta aquí o haga clic para seleccionarlo.',
    },
    image: {
      upload_several: 'Arrastre algunas imágenes hasta aquí o haga clic para seleccionar una.',
      upload_single: 'Arrastre una imagen hasta aquí o haga clic para seleccionarla.',
    },
    references: {
      all_missing: 'No se han podido encontrar las referencias.',
      many_missing: 'Faltan al menos una de las referencias asociadas.',
      single_missing: 'Falta una referencia asociada.',
    },
    password: {
      toggle_visible: 'Ocultar contraseña',
      toggle_hidden: 'Mostrar contraseña',
    },
  },
  page: {
    create: 'Crear %{name}',
    dashboard: 'Panel de control',
    edit: '%{name} #%{recordRepresentation}',
    error: 'Se ha producido un error',
    list: '%{name}',
    loading: 'Cargando',
    not_found: 'No encontrado',
    show: '%{name} #%{recordRepresentation}',
    empty: 'Aún no hay %{name}.',
    invite: '¿Desea añadir uno?',
    access_denied: 'Acceso denegado',
    authentication_error: 'No se puede autenticar la solicitud',
  },
  notification: {
    updated: 'Elemento actualizado |||| %{smart_count} elementos actualizados',
    created: 'Elemento creado',
    deleted: 'Elemento eliminado |||| %{smart_count} elementos eliminados',
    bad_item: 'Elemento incorrecto',
    item_doesnt_exist: 'El elemento no existe',
    http_error: 'Error de comunicación con el servidor',
    data_provider_error: 'Error del proveedor de datos. Compruebe la consola para más detalles.',
    i18n_error: 'No se han podido cargar las traducciones para el idioma indicado',
    canceled: 'Acción cancelada',
    logged_out: 'Su sesión ha terminado, por favor vuelva a iniciar sesión.',
    not_authorized: 'No tiene permisos para acceder a este recurso.',
    application_update_available: 'Hay una nueva versión disponible.',
  },
};

const es = {
  ...spanishMessages,
  ra: {
    ...spanishMessages.ra,
    ...v5RaSpanishOverrides,
    page: {
      ...spanishMessages.ra.page,
      ...v5RaSpanishOverrides.page,
      dashboard: 'Panel de Control',
    },
  },
  common,
  auth,
  dashboard,
  onboarding,
  fields: {
    name: 'Nombre',
    description: 'Descripción',
    status: 'Estado',
    email: 'Email',
    createdAt: 'Fecha de creación',
    updatedAt: 'Última actualización',
  },
  resources: {
    ...qrphoneResources,
  },
  user: {
    profile: 'Perfil',
    settings: 'Configuración',
    change_password: 'Cambiar contraseña',
  },
  pages: {
    profile: {
      title: 'Mi perfil',
      data: 'Datos personales',
      saved: 'Perfil actualizado correctamente',
      change_password: 'Cambiar contraseña',
      current_password: 'Contraseña actual',
      new_password: 'Nueva contraseña',
      confirm_password: 'Confirme la nueva contraseña',
      password_changed: 'Contraseña cambiada correctamente',
      password_mismatch: 'Las contraseñas no coinciden',
    },
    system: {
      title: 'Sistema',
      tabs: {
        health: 'Salud del servicio',
        devices: 'Dispositivos',
        routing: 'Salud del enrutado',
      },
      routing: {
        intro:
          'Un kiosco solo puede recibir llamadas si su personal por defecto es un agente con un dispositivo activo. Aquí se listan los kioscos con el enrutado mal configurado.',
        all_ok: 'Todos los kioscos activos tienen un personal por defecto que puede atender llamadas.',
        summary: '%{broken} de %{total} kioscos activos tienen el enrutado mal configurado.',
        kiosk: 'Kiosco',
        organization: 'Organización',
        default_staff: 'Personal por defecto',
        problem: 'Problema',
        status: {
          no_default: 'Sin personal por defecto',
          default_not_agent: 'El personal por defecto no es un agente válido',
          default_no_device: 'El personal por defecto no tiene dispositivo',
        },
      },
      health: {
        ok: 'Operativo',
        down: 'No disponible',
        version: 'Versión',
        uptime: 'Tiempo en marcha',
        refresh_note: 'Los datos se actualizan automáticamente cada 5 segundos.',
        counters: {
          activeRooms: 'Salas activas',
          busyParticipants: 'Participantes en llamada',
          ringingStaff: 'Personal sonando',
          queuedCalls: 'Llamadas en espera',
          watchedCalls: 'Llamadas supervisadas',
          pendingTimers: 'Temporizadores pendientes',
        },
      },
      devices: {
        name: 'Nombre',
        email: 'Correo electrónico',
        role: 'Rol',
        organization: 'Organización',
        devices: 'Dispositivos',
        status: 'Estado',
        none: 'Sin dispositivos',
        unknown: 'Desconocido',
        can_receive: 'Puede recibir llamadas',
        cannot_receive: 'No puede recibir llamadas',
      },
    },
    serverSettings: {
      title: 'Ajustes del servidor',
      saved: 'Configuración guardada correctamente',
      secret_note: 'Los campos secretos no muestran el valor almacenado: déjelos en blanco para mantenerlo.',
      secret_placeholder: 'Sin cambios',
      tabs: {
        callserver: 'Servidor de llamadas',
        mail: 'Correo y pagos',
      },
      callserver: {
        servertype: 'Tipo de servidor',
        type_custom: 'Servidor propio',
        type_hosted: 'Proveedor gestionado',
        url: 'URL del servidor',
        username: 'Usuario',
        password: 'Contraseña',
        account_sid: 'Identificador de cuenta',
        auth_token: 'Token de autenticación',
      },
      mail: {
        server: 'Servidor de correo',
        port: 'Puerto',
        user: 'Usuario',
        sender: 'Remitente',
        password: 'Contraseña / clave API de correo',
        payments_key: 'Clave API de pagos',
      },
      payments: {
        title: 'Pagos',
        description:
          'Decide cómo se dan de alta los nuevos clientes en esta instancia.',
        mode: 'Modo de funcionamiento',
        mode_linked: 'Enlazado a la plataforma de pagos',
        mode_manual: 'Alta manual',
        mode_help_linked:
          'El visitante elige un plan y se le lleva a la plataforma de pagos para completar el cobro.',
        mode_help_manual:
          'No se cobra en línea. El visitante elige un plan y deja una solicitud que usted resuelve desde la lista de solicitudes de alta.',
        platform_url: 'Dirección de la plataforma de pagos',
        platform_client_id: 'Identificador de cliente',
        platform_secret: 'Secreto de acceso',
        request_email: 'Correo para las solicitudes de alta',
        request_email_help:
          'Dirección a la que se avisa de cada nueva solicitud. Si la deja en blanco, las solicitudes se siguen registrando pero no se envía aviso.',
        legacy_key_help:
          'Credencial de la integración de cobro anterior. Se mantiene mientras se completa el traspaso a la plataforma de pagos.',
      },
    },
    login: {
      subtitle: 'Panel de administración',
      login_button: 'Iniciar sesión',
      forgot_password: '¿Ha olvidado su contraseña?',
    },
    dashboard: {
      welcome: 'Bienvenido, %{name}',
      intro: 'Seleccione una sección del menú para empezar.',
      calls_today: 'Llamadas hoy',
      calls_week: 'Llamadas (últimos 7 días)',
      clients: 'Clientes',
      recent_calls: 'Últimas llamadas',
      service_status: 'Estado del servicio',
      service_ok: 'Operativo',
      service_down: 'No disponible',
      active_calls: 'Llamadas activas',
      queued_calls: 'Llamadas en cola',
      no_data: 'No se encontraron resultados',
      no_data_description: 'No hay datos disponibles en este momento',
      desktop_window_title: 'Ventana de escritorio para las llamadas',
      desktop_window_body:
        'El softphone puede instalarse como una ventana de escritorio propia: avisa de las llamadas con la ventana minimizada, dispone de icono propio en la barra de tareas y, si así lo desea, puede iniciarse automáticamente al arrancar el equipo. Para abrirlo, utilice el botón «Recibir llamadas» de la barra superior. Dentro, en la pantalla de personal, encontrará las opciones «Instalar aplicación» y «Activar inicio automático».',
    },
  },
  validation: common.validation,
  notifications: common.notifications,
};

export default es;
